import { EmptyState, ListSkeleton } from '@atlas/ui-components'
import { Button, DialogContent, DialogTitle } from '@atlas/ui-primitives'
import type { Message } from '@/domain/message.types'

export interface MessagePickerDialogProps {
	isOpen: boolean
	candidates: readonly Message[]
	isLoading: boolean
	isError: boolean
	search: string
	onSearchChange: (value: string) => void
	selectedIds: ReadonlySet<string>
	onToggleSelection: (messageId: string) => void
	isAssociationPending: boolean
	isAssociationError: boolean
	isAssociationSuccess: boolean
	onConfirm: () => void
	onRetry: () => void
	onCancel: () => void
}

function CandidateList({
	candidates,
	selectedIds,
	onToggleSelection,
}: {
	candidates: readonly Message[]
	selectedIds: ReadonlySet<string>
	onToggleSelection: (messageId: string) => void
}) {
	return (
		<ul
			role="listbox"
			aria-multiselectable="true"
			aria-label="Candidate Messages"
			className="max-h-60 space-y-1 overflow-auto"
		>
			{candidates.map((message) => (
				<li key={message.messageId}>
					<label className="flex items-start gap-2 rounded-ds-sm px-2 py-1.5 text-sm text-ds-text hover:bg-ds-surface">
						<input
							type="checkbox"
							checked={selectedIds.has(message.messageId)}
							onChange={() => onToggleSelection(message.messageId)}
							className="mt-1"
						/>
						<span className="min-w-0 flex-1">
							<span className="flex items-baseline gap-2">
								{message.title}
								<span className="text-xs text-ds-text-subtle">({message.languageIso})</span>
							</span>
							{message.content && <span className="block truncate text-xs text-ds-text-muted">{message.content}</span>}
						</span>
					</label>
				</li>
			))}
		</ul>
	)
}

function PickerFooter({
	selectionCount,
	isAssociationError,
	isAssociationPending,
	onConfirm,
	onRetry,
	onCancel,
}: {
	selectionCount: number
	isAssociationError: boolean
	isAssociationPending: boolean
	onConfirm: () => void
	onRetry: () => void
	onCancel: () => void
}) {
	return (
		<div className="flex justify-end gap-3">
			<Button variant="outline" onPress={onCancel}>
				Cancel
			</Button>
			{isAssociationError ? (
				<Button onPress={onRetry}>Retry</Button>
			) : (
				<Button onPress={onConfirm} isDisabled={selectionCount === 0 || isAssociationPending}>
					{isAssociationPending ? 'Associating…' : 'Confirm'}
				</Button>
			)}
		</div>
	)
}

/**
 * Existing-Messages association picker, shared by every surface that associates
 * copy it does not own — a Campaign (`campaign_code`) and a work order
 * (`order_code`). Receives candidates (already server-side search-filtered by the
 * owning container), selection state, search state, and association state
 * entirely as props - calls no hook, imports no fixture. Association is
 * additive-only; a failure can be retried without re-selecting Messages.
 */
export function MessagePickerDialog({
	isOpen,
	candidates,
	isLoading,
	isError,
	search,
	onSearchChange,
	selectedIds,
	onToggleSelection,
	isAssociationPending,
	isAssociationError,
	isAssociationSuccess,
	onConfirm,
	onRetry,
	onCancel,
}: MessagePickerDialogProps) {
	return (
		<DialogContent size="lg" isOpen={isOpen} isKeyboardDismissDisabled={isAssociationPending} className="space-y-4">
			<DialogTitle>Browse existing Messages</DialogTitle>

			<input
				type="text"
				value={search}
				onChange={(event) => onSearchChange(event.target.value)}
				placeholder="Search Messages"
				aria-label="Search Messages"
				className="w-full rounded-ds-md border border-ds-border bg-ds-surface px-3 py-2 text-sm text-ds-text"
			/>

			{isLoading && <ListSkeleton rows={3} label="Searching messages" />}
			{isError && (
				<p role="alert" className="text-sm text-ds-danger">
					Unable to search for Messages.
				</p>
			)}
			{!isLoading && !isError && candidates.length === 0 && (
				<EmptyState title="No Messages match your search." variant="filtered" />
			)}
			{!isLoading && !isError && candidates.length > 0 && (
				<CandidateList candidates={candidates} selectedIds={selectedIds} onToggleSelection={onToggleSelection} />
			)}

			<p className="text-sm text-ds-text-muted">{selectedIds.size} selected</p>

			{isAssociationError && (
				<p role="alert" className="text-sm text-ds-danger">
					Associating Messages failed.
				</p>
			)}
			{isAssociationSuccess && (
				<p role="status" className="text-sm text-ds-success">
					Messages associated.
				</p>
			)}

			<PickerFooter
				selectionCount={selectedIds.size}
				isAssociationError={isAssociationError}
				isAssociationPending={isAssociationPending}
				onConfirm={onConfirm}
				onRetry={onRetry}
				onCancel={onCancel}
			/>
		</DialogContent>
	)
}
